import React, { useState, useEffect, useCallback } from 'react';
import { TableSkeleton } from '../../components/enterprise';
import {
    Box, Typography, Paper, Button, Chip, TextField, CircularProgress,
    useTheme, Alert,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
    Refresh as RefreshIcon, Download as DownloadIcon,
} from '@mui/icons-material';
import billingService from '../../services/billingService';
import { toServerDate, zonedDayKey } from '../../timezone';

const actionColors = {
    CREATE: 'success', DELETE: 'error', UPDATE: 'primary', LOGIN: 'default',
    ENABLE: 'success', DISABLE: 'warning',
};

const getActionColor = (action) => {
    if (!action) return 'default';
    for (const [key, color] of Object.entries(actionColors)) {
        if (action.toUpperCase().includes(key)) return color;
    }
    return 'default';
};

const AuditLogPage = () => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [totalElements, setTotalElements] = useState(0);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(50);
    const [message, setMessage] = useState({ type: '', text: '' });

    // Filters
    const [actionFilter, setActionFilter] = useState('');
    const [usernameFilter, setUsernameFilter] = useState('');
    const [resourceTypeFilter, setResourceTypeFilter] = useState('');

    const loadLogs = useCallback(async () => {
        setLoading(true);
        try {
            const params = { page, size: pageSize };
            if (actionFilter) params.action = actionFilter;
            if (usernameFilter) params.username = usernameFilter;
            if (resourceTypeFilter) params.resourceType = resourceTypeFilter;

            const res = await billingService.getAuditLogs(params);
            setLogs(res.data?.content || []);
            setTotalElements(res.data?.totalElements || 0);
        } catch {
            setMessage({ type: 'error', text: 'Failed to load audit logs' });
        } finally {
            setLoading(false);
        }
    }, [page, pageSize, actionFilter, usernameFilter, resourceTypeFilter]);

    useEffect(() => { loadLogs(); }, [loadLogs]);

    const handleExport = async () => {
        try {
            const res = await billingService.exportAuditLogs({});
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `audit_log_${zonedDayKey(Date.now())}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch {
            setMessage({ type: 'error', text: 'Failed to export audit logs' });
        }
    };

    const columns = [
        {
            field: 'createdAt', headerName: 'Timestamp', width: 170,
            renderCell: (p) => toServerDate(p.value)?.toLocaleString() ?? '-',
        },
        { field: 'username', headerName: 'User', width: 130, renderCell: (p) => p.value || 'System' },
        {
            field: 'action', headerName: 'Action', width: 160,
            renderCell: (p) => (
                <Chip label={p.value || 'Unknown'} size="small" color={getActionColor(p.value)} variant="outlined" />
            ),
        },
        { field: 'resourceType', headerName: 'Resource Type', width: 140, renderCell: (p) => p.value || '-' },
        { field: 'resourceId', headerName: 'Resource ID', width: 120, renderCell: (p) => p.value || '-' },
        {
            field: 'details', headerName: 'Details', flex: 1, minWidth: 200,
            renderCell: (p) => (
                <Typography variant="caption" sx={{ color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {p.value || '-'}
                </Typography>
            ),
        },
        { field: 'ipAddress', headerName: 'IP Address', width: 130, renderCell: (p) => p.value || '-' },
    ];

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>Audit Log</Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Track all platform actions — user creation, org changes, logins, and configuration updates.
                </Typography>
            </Box>

            {message.text && (
                <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage({ type: '', text: '' })}>{message.text}</Alert>
            )}

            {/* Filters */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField label="Action" value={actionFilter} onChange={(e) => { setActionFilter(e.target.value); setPage(0); }} size="small" InputLabelProps={{ shrink: true }} sx={{ width: 160 }} placeholder="e.g. CREATE" />
                <TextField label="Username" value={usernameFilter} onChange={(e) => { setUsernameFilter(e.target.value); setPage(0); }} size="small" InputLabelProps={{ shrink: true }} sx={{ width: 160 }} />
                <TextField label="Resource Type" value={resourceTypeFilter} onChange={(e) => { setResourceTypeFilter(e.target.value); setPage(0); }} size="small" InputLabelProps={{ shrink: true }} sx={{ width: 160 }} placeholder="e.g. Organization" />
                <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={loadLogs}>Refresh</Button>
                <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={handleExport}>Export CSV</Button>
            </Box>

            <Paper sx={{ border: `1px solid ${theme.palette.divider}` }}>
                {loading ? (
                    <TableSkeleton rows={5} columns={4} />
                ) : (
                    <DataGrid
                        rows={logs} columns={columns} autoHeight
                        paginationMode="server"
                        rowCount={totalElements}
                        page={page}
                        pageSize={pageSize}
                        onPageChange={(newPage) => setPage(newPage)}
                        onPageSizeChange={(newSize) => { setPageSize(newSize); setPage(0); }}
                        rowsPerPageOptions={[25, 50, 100]}
                        disableRowSelectionOnClick
                        getRowId={(row) => row.id}
                        sx={{
                            border: 'none',
                            '& .MuiDataGrid-cell': { borderColor: theme.palette.divider },
                            '& .MuiDataGrid-columnHeaders': { borderColor: theme.palette.divider, bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' },
                            '& .MuiDataGrid-row:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)' },
                        }}
                    />
                )}
            </Paper>
        </Box>
    );
};

export default AuditLogPage;
